import { defineStore } from 'pinia';
import { ref } from 'vue';

import { isBoolean, isNumber, isRecord } from '@/utils/typeGuards';
import { readVersionedLocalStorage, writeVersionedLocalStorage } from '@/utils/versionedLocalStorage';


const ACCESS = 'access';
const VERSION = 2;

type AccessConf = {
	allowReadOnlyUnauthorizedAccess: boolean;
	showMountpointsForUnauthorizedUsers: boolean;
	telegramAlertsEnabled: boolean;
	smtpReady: boolean;
	version: number;
};

type AccessConfPatch = Partial<Omit<AccessConf, 'version'>>;


const defaultAccessConf = (): AccessConf => ({
	allowReadOnlyUnauthorizedAccess: false,
	showMountpointsForUnauthorizedUsers: false,
	telegramAlertsEnabled: false,
	smtpReady: false,
	version: VERSION,
});


const isAccessConf = (value: unknown): value is AccessConf => {
	if (!isRecord(value)) {
		return false;
	}
	return (
		isNumber(value.version) &&
		isBoolean(value.allowReadOnlyUnauthorizedAccess) &&
		isBoolean(value.showMountpointsForUnauthorizedUsers) &&
		isBoolean(value.telegramAlertsEnabled) &&
		isBoolean(value.smtpReady)
	);
};


const readAccessConf = (): AccessConf => (
	readVersionedLocalStorage(ACCESS, VERSION, isAccessConf, defaultAccessConf())
);


const writeAccessConf = (data: AccessConfPatch): void => {
	writeVersionedLocalStorage(ACCESS, VERSION, readAccessConf, data);
};


export const useAccessStore = defineStore('access', () => {
	const accessConf = readAccessConf();
	const allowReadOnlyUnauthorizedAccess = ref(accessConf.allowReadOnlyUnauthorizedAccess);
	const showMountpointsForUnauthorizedUsers = ref(accessConf.showMountpointsForUnauthorizedUsers);
	const telegramAlertsEnabled = ref(accessConf.telegramAlertsEnabled);
	const smtpReady = ref(accessConf.smtpReady);

	const update = (
		newAllowReadOnlyUnauthorizedAccess: boolean,
		newShowMountpointsForUnauthorizedUsers: boolean,
		newTelegramAlertsEnabled: boolean,
		newSmtpReady: boolean,
	): void => {
		allowReadOnlyUnauthorizedAccess.value = newAllowReadOnlyUnauthorizedAccess;
		showMountpointsForUnauthorizedUsers.value = newShowMountpointsForUnauthorizedUsers;
		telegramAlertsEnabled.value = newTelegramAlertsEnabled;
		smtpReady.value = newSmtpReady;
		writeAccessConf({
			allowReadOnlyUnauthorizedAccess: newAllowReadOnlyUnauthorizedAccess,
			showMountpointsForUnauthorizedUsers: newShowMountpointsForUnauthorizedUsers,
			telegramAlertsEnabled: newTelegramAlertsEnabled,
			smtpReady: newSmtpReady,
		});
	};

	const updateSmtpReady = (newSmtpReady: boolean): void => {
		smtpReady.value = newSmtpReady;
		writeAccessConf({ smtpReady: newSmtpReady });
	};

	return {
		allowReadOnlyUnauthorizedAccess,
		showMountpointsForUnauthorizedUsers,
		telegramAlertsEnabled,
		smtpReady,
		update,
		updateSmtpReady,
	};
});
